import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { CsvService } from '@app/core/services/csv.service';
import { AlertService } from '@app/core/services/alert.service';
import { ModalService } from '@app/core/services/modal.service';

interface CorrectionConfig {
  label: string;
  monthsOffset: number;
  businessReasonCode: string;
  processVariant: string | null;
  filenameSuffix: string;
}

@Component({
  selector: 'app-settlement-wholesale-export-report-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    TranslatePipe,
    ToggleComponent,
  ],
  templateUrl: './settlement-wholesale-export-report-modal.component.html',
  styleUrl: './settlement-wholesale-export-report-modal.component.css'
})
export class SettlementWholesaleExportReportModalComponent {
  public form = new FormGroup({
    startDate: new FormControl<string>('', { nonNullable: true }),
    endDate: new FormControl<string>('', { nonNullable: true }),
    korrektion1: new FormControl<boolean>(false, { nonNullable: true }),
    korrektion2: new FormControl<boolean>(false, { nonNullable: true }),
    korrektion3: new FormControl<boolean>(false, { nonNullable: true }),
  });

  public readonly engrosfiksering: CorrectionConfig = {
    label: 'Engrosfiksering (D05)', monthsOffset: 0, businessReasonCode: 'D05', processVariant: null, filenameSuffix: 'Engrosfiksering',
  };

  public readonly korrektioner: CorrectionConfig[] = [
    { label: '1. korrektion', monthsOffset: 3, businessReasonCode: 'D32', processVariant: 'D01', filenameSuffix: '1_korrektion' },
    { label: '2. korrektion', monthsOffset: 18, businessReasonCode: 'D32', processVariant: 'D02', filenameSuffix: '2_korrektion' },
    { label: '3. korrektion', monthsOffset: 30, businessReasonCode: 'D32', processVariant: 'D03', filenameSuffix: '3_korrektion' },
  ];

  private isExporting = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly csvService: CsvService,
    private readonly alertService: AlertService,
    private readonly modalService: ModalService,
  ) {}

  public getCorrectionDateRange(monthsOffset: number): { start: string; end: string } | null {
    const startDate = this.form.value.startDate;
    const endDate = this.form.value.endDate;

    if (!startDate || !endDate) {
      return null;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    start.setMonth(start.getMonth() - monthsOffset);
    end.setMonth(end.getMonth() - monthsOffset);

    return {
      start: this.formatDate(start),
      end: this.formatDate(end),
    };
  }

  public submit(): void {
    if (this.isExporting) return;

    const startDate = this.form.value.startDate;
    const endDate = this.form.value.endDate;

    if (!startDate || !endDate) {
      return;
    }

    const selectedCorrections: CorrectionConfig[] = [this.engrosfiksering];

    if (this.form.value.korrektion1) {
      selectedCorrections.push(this.korrektioner[0]);
    }
    if (this.form.value.korrektion2) {
      selectedCorrections.push(this.korrektioner[1]);
    }
    if (this.form.value.korrektion3) {
      selectedCorrections.push(this.korrektioner[2]);
    }

    this.isExporting = true;
    this.downloadSequentially(selectedCorrections, 0);
  }

  private downloadSequentially(corrections: CorrectionConfig[], index: number): void {
    if (index >= corrections.length) {
      this.isExporting = false;
      this.modalService.close();
      return;
    }

    const correction = corrections[index];
    const dateRange = this.getCorrectionDateRange(correction.monthsOffset);
    if (!dateRange) {
      this.downloadSequentially(corrections, index + 1);
      return;
    }

    const body: Record<string, any> = {
      startDate: dateRange.start,
      endDate: dateRange.end,
      businessReasonCode: correction.businessReasonCode,
    };

    if (correction.processVariant) {
      body['processVariant'] = correction.processVariant;
    }

    this.httpClient.post('/api/dmc/Settlement/exportWholesaleReport', body, {
      responseType: 'blob',
      observe: 'response',
    }).subscribe({
      next: (response: HttpResponse<Blob>) => {
        const filename = this.getFilenameFromContentDisposition(response.headers.get('content-disposition'))
          ?? `${correction.filenameSuffix}_${dateRange.start}_${dateRange.end}.csv`;
        if (response.body) {
          this.csvService.downloadFromBlob(response.body, filename);
        }
        setTimeout(() => this.downloadSequentially(corrections, index + 1), 500);
      },
      error: () => {
        this.alertService.open('global', 'danger', 'settlement.wholesale.exportReport.error');
        this.isExporting = false;
      }
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getFilenameFromContentDisposition(header: string | null): string | null {
    if (!header) {
      return null;
    }

    const filenameStarRegex = /filename\*\s*=\s*UTF-8''([^;]+)/i;
    const filenameStarMatch = filenameStarRegex.exec(header);
    if (filenameStarMatch) {
      return decodeURIComponent(filenameStarMatch[1]);
    }

    const filenameRegex = /filename\s*=\s*"?([^;"]+)"?/i;
    const filenameMatch = filenameRegex.exec(header);
    return filenameMatch ? filenameMatch[1] : null;
  }
}
